(function () {
  const INTRO_DURATION = 1.35;
  const INTRO_STAGGER = 0.12;
  const INTRO_OFFSET_VH = 85;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getCssNumber(el, variableName, fallback) {
    const raw = getComputedStyle(el).getPropertyValue(variableName).trim();
    const number = parseFloat(raw);
    return Number.isFinite(number) ? number : fallback;
  }

  function getOverlayFinal(root) {
    const raw = getComputedStyle(root).getPropertyValue('--koLee-overlay-final').trim();
    const value = parseFloat(raw);

    if (!Number.isFinite(value)) return 0.8;

    if (value > 1) {
      return clamp(value / 100, 0, 0.95);
    }

    return clamp(value, 0, 0.95);
  }

  function getOverlayColor(root) {
    const raw = getComputedStyle(root).getPropertyValue('--koLee-overlay').trim();
    return raw || '#ffffff';
  }

  function getColumnCount(root) {
    const isMobile = window.matchMedia('(max-width: 900px)').matches;

    if (isMobile) {
      return Math.max(1, Math.round(getCssNumber(root, '--koLee-cols-mobile', 3)));
    }

    return Math.max(1, Math.round(getCssNumber(root, '--koLee-cols-desktop', 6)));
  }

  function rebuildColumns(root) {
    const grid = root.querySelector('.koLeeBrand__videoGrid');
    if (!grid) return [];

    let tiles = Array.from(grid.querySelectorAll('.koLeeBrand__tile'));

    if (grid.querySelector('.koLeeBrand__column')) {
      tiles = Array.from(grid.querySelectorAll('.koLeeBrand__tile:not([data-ko-lee-clone="true"])'));
      grid.innerHTML = '';
    }

    if (!tiles.length) return [];

    const columnCount = getColumnCount(root);
    const columns = [];

    grid.style.gridTemplateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;

    for (let i = 0; i < columnCount; i += 1) {
      const column = document.createElement('div');
      column.className = 'koLeeBrand__column';
      column.setAttribute('data-ko-lee-column', String(i));

      grid.appendChild(column);
      columns.push(column);
    }

    tiles.forEach((tile, index) => {
      tile.removeAttribute('data-ko-lee-clone');
      tile.setAttribute('data-ko-lee-original', 'true');

      const columnIndex = index % columnCount;
      columns[columnIndex].appendChild(tile);
    });

    columns.forEach((column) => {
      const originalTiles = Array.from(column.children);

      originalTiles.forEach((tile) => {
        const clone = tile.cloneNode(true);
        clone.setAttribute('data-ko-lee-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        column.appendChild(clone);
      });
    });

    return columns;
  }

  function playVideos(root) {
    root.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.playsInline = true;

      const playPromise = video.play();

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    });
  }

  function getLoopStartY(column, index) {
    const distance = column.scrollHeight / 2;
    const directionUp = index % 2 === 0;

    return directionUp ? 0 : -distance;
  }

  function animateColumnsForever(columns) {
    if (!columns.length || !window.gsap) return;

    columns.forEach((column, index) => {
      const distance = column.scrollHeight / 2;
      if (!distance) return;

      const directionUp = index % 2 === 0;
      const duration = 24 + (index % 3) * 5;

      gsap.killTweensOf(column);

      if (directionUp) {
        gsap.fromTo(
          column,
          { y: 0 },
          {
            y: -distance,
            duration: duration,
            ease: 'none',
            repeat: -1,
          },
        );
      } else {
        gsap.fromTo(
          column,
          { y: -distance },
          {
            y: 0,
            duration: duration,
            ease: 'none',
            repeat: -1,
          },
        );
      }
    });
  }

  function introColumns(columns) {
    const introOffset = window.innerHeight * (INTRO_OFFSET_VH / 100);

    const introTl = gsap.timeline({
      defaults: {
        ease: 'power3.out',
      },
      onComplete: function () {
        animateColumnsForever(columns);
      },
    });

    columns.forEach((column, index) => {
      const baseY = getLoopStartY(column, index);
      const comesFromBottom = index % 2 === 0;
      const introY = comesFromBottom ? baseY + introOffset : baseY - introOffset;

      gsap.set(column, {
        y: introY,
        autoAlpha: 1,
      });
    });

    introTl.to(columns, {
      y: function (index, column) {
        return getLoopStartY(column, index);
      },
      duration: INTRO_DURATION,
      stagger: {
        each: INTRO_STAGGER,
        from: 'start',
      },
    });

    return introTl;
  }

  function createRuntimeOverlay(root, stage) {
    const oldOverlay = root.querySelector('.koLeeBrand__overlay');

    if (oldOverlay) {
      oldOverlay.style.display = 'none';
    }

    let runtimeOverlay = stage.querySelector('.koLeeBrand__runtimeOverlay');

    if (!runtimeOverlay) {
      runtimeOverlay = document.createElement('div');
      runtimeOverlay.className = 'koLeeBrand__runtimeOverlay';
      stage.appendChild(runtimeOverlay);
    }

    runtimeOverlay.style.position = 'absolute';
    runtimeOverlay.style.inset = '0';
    runtimeOverlay.style.zIndex = '5000';
    runtimeOverlay.style.display = 'block';
    runtimeOverlay.style.visibility = 'visible';
    runtimeOverlay.style.pointerEvents = 'none';
    runtimeOverlay.style.opacity = '0';
    runtimeOverlay.style.background = getOverlayColor(root);

    return runtimeOverlay;
  }

  function setupRuntimeScrollReveal(root, overlay, logoWrap, caption) {
    const overlayFinal = getOverlayFinal(root);

    let ticking = false;

    function update() {
      ticking = false;

      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const totalScrollable = Math.max(root.offsetHeight - viewportHeight, 1);
      const scrolledInside = clamp(-rect.top, 0, totalScrollable);
      const progress = scrolledInside / totalScrollable;

      const overlayOpacity = progress * overlayFinal;

      overlay.style.opacity = String(overlayOpacity);

      if (logoWrap) {
        const logoProgress = clamp((progress - 0.14) / 0.42, 0, 1);

        logoWrap.style.opacity = String(logoProgress);
        logoWrap.style.visibility = logoProgress > 0.01 ? 'visible' : 'hidden';

        const y = 20 + -70 * logoProgress;
        const scale = 0.94 + 0.06 * logoProgress;

        logoWrap.style.transform = `translate(-50%, ${y}%) scale(${scale})`;
        logoWrap.style.zIndex = '5001';
      }

      if (caption) {
        const captionProgress = clamp((progress - 0.42) / 0.28, 0, 1);

        caption.style.opacity = String(captionProgress);
        caption.style.transform = `translateY(${18 * (1 - captionProgress)}px)`;
      }
    }

    function requestUpdate() {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(update);
    }

    update();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('load', requestUpdate);
  }

  function setupScrollCursor(stage) {
    if (!stage) return;

    const finePointer = window.matchMedia('(pointer: fine)').matches;
    if (!finePointer) return;

    let cursor = document.querySelector('.koLeeBrandScrollCursor');

    if (!cursor) {
      cursor = document.createElement('div');
      cursor.className = 'koLeeBrandScrollCursor';
      cursor.setAttribute('aria-hidden', 'true');
      cursor.innerHTML = `
      <span class="koLeeBrandScrollCursor__ring"></span>
      <span class="koLeeBrandScrollCursor__wheel"></span>
      <span class="koLeeBrandScrollCursor__chevron"></span>
    `;
      document.body.appendChild(cursor);
    }

    let cursorX = 0;
    let cursorY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = null;
    let isInside = false;

    function animateCursor() {
      currentX += (cursorX - currentX) * 0.22;
      currentY += (cursorY - currentY) * 0.22;

      cursor.style.left = `${currentX}px`;
      cursor.style.top = `${currentY}px`;

      if (isInside) {
        rafId = window.requestAnimationFrame(animateCursor);
      } else {
        rafId = null;
      }
    }

    function showCursor(event) {
      isInside = true;
      cursorX = event.clientX;
      cursorY = event.clientY;
      currentX = event.clientX;
      currentY = event.clientY;

      stage.classList.add('is-scroll-cursor-active');
      cursor.classList.add('is-visible');

      if (!rafId) {
        rafId = window.requestAnimationFrame(animateCursor);
      }
    }

    function moveCursor(event) {
      cursorX = event.clientX;
      cursorY = event.clientY;
    }

    function hideCursor() {
      isInside = false;

      stage.classList.remove('is-scroll-cursor-active');
      cursor.classList.remove('is-visible');

      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    stage.addEventListener('pointerenter', showCursor);
    stage.addEventListener('pointermove', moveCursor);
    stage.addEventListener('pointerleave', hideCursor);

    window.addEventListener('blur', hideCursor);
    window.addEventListener(
      'scroll',
      function () {
        const rect = stage.getBoundingClientRect();
        const isStageVisible = rect.bottom > 0 && rect.top < window.innerHeight;

        if (!isStageVisible) {
          hideCursor();
        }
      },
      { passive: true },
    );
  }

  function setupVideoReadyStates(root) {
    if (!root) return;

    const videos = root.querySelectorAll('.koLeeBrand__tile video');

    videos.forEach(function (video) {
      const tile = video.closest('.koLeeBrand__tile');
      if (!tile) return;

      function markReady() {
        tile.classList.add('is-video-ready');
      }

      if (video.readyState >= 2) {
        markReady();
      } else {
        video.addEventListener('loadeddata', markReady, { once: true });
        video.addEventListener('canplay', markReady, { once: true });
      }
    });
  }

  function initKoLeeBrand() {
    const root = document.querySelector('[data-ko-lee-brand]');
    if (!root) return;
    document.documentElement.classList.add('has-ko-lee-brand');
    document.body.classList.add('has-ko-lee-brand');
    if (!window.gsap) {
      console.warn('GSAP not loaded for Ko-Lee page.');
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const stage = root.querySelector('.koLeeBrand__stage');
    const logoWrap = root.querySelector('.koLeeBrand__logoWrap');
    const caption = root.querySelector('.koLeeBrand__caption');

    if (!stage) return;

    setupScrollCursor(stage);

    stage.style.isolation = 'isolate';

    const overlay = createRuntimeOverlay(root, stage);

    const columns = rebuildColumns(root);
    if (!columns.length) return;

    playVideos(root);
    setupVideoReadyStates(root);

    if (logoWrap) {
      logoWrap.style.opacity = '0';
      logoWrap.style.visibility = 'hidden';
      logoWrap.style.zIndex = '5001';
      logoWrap.style.transform = 'translate(-50%, 20%) scale(.94)';
    }

    if (caption) {
      caption.style.opacity = '0';
      caption.style.transform = 'translateY(18px)';
    }

    if (reducedMotion) {
      columns.forEach((column, index) => {
        gsap.set(column, {
          autoAlpha: 1,
          y: getLoopStartY(column, index),
        });
      });

      overlay.style.opacity = String(getOverlayFinal(root));

      if (logoWrap) {
        logoWrap.style.opacity = '1';
        logoWrap.style.visibility = 'visible';
        logoWrap.style.transform = 'translate(-50%, -50%) scale(1)';
      }

      return;
    }

    introColumns(columns);
    setupRuntimeScrollReveal(root, overlay, logoWrap, caption);

    window.addEventListener('load', function () {
      playVideos(root);
      setupVideoReadyStates(root);
    });
  }

  document.addEventListener('DOMContentLoaded', initKoLeeBrand);

  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector('[data-ko-lee-brand]')) return;
    initKoLeeBrand();
  });
})();
(function () {
  function setupKoLeeNav(nav) {
    if (!nav || nav.dataset.koLeeNavReady === 'true') return;

    nav.dataset.koLeeNavReady = 'true';

    const brandSection =
      nav.previousElementSibling && nav.previousElementSibling.matches('[data-ko-lee-brand]')
        ? nav.previousElementSibling
        : document.querySelector('[data-ko-lee-brand]');

    if (!brandSection) return;

    let spacer = nav.previousElementSibling;

    if (!spacer || !spacer.classList || !spacer.classList.contains('koLeeBrandNav__spacer')) {
      spacer = document.createElement('div');
      spacer.className = 'koLeeBrandNav__spacer';
      nav.parentNode.insertBefore(spacer, nav);
    }

    let lastY = window.pageYOffset || document.documentElement.scrollTop || 0;
    let navHeight = 0;
    let brandEndY = 0;
    let ticking = false;

    function measure() {
      const wasFixed = nav.classList.contains('is-fixed');

      nav.classList.remove('is-fixed');
      nav.classList.remove('is-hidden');
      spacer.style.height = '0px';

      navHeight = nav.offsetHeight;

      /*
        This is the important part:
        the navbar is not allowed to appear as fixed until
        the entire video wall / logo animation section has ended.
      */
      brandEndY = brandSection.getBoundingClientRect().bottom + window.pageYOffset;

      if (wasFixed) {
        spacer.style.height = navHeight + 'px';
        nav.classList.add('is-fixed');
      }
    }

    function unfixNav() {
      nav.classList.remove('is-fixed');
      nav.classList.remove('is-hidden');
      spacer.style.height = '0px';
    }

    function fixNav() {
      nav.classList.add('is-fixed');
      spacer.style.height = navHeight + 'px';
    }

    function update() {
      const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const delta = currentY - lastY;

      /*
        Before the video wall finishes:
        navbar must stay in normal document flow after the video section.
        It must NOT be fixed at the top.
      */
      if (currentY < brandEndY) {
        unfixNav();
        lastY = Math.max(currentY, 0);
        ticking = false;
        return;
      }

      /*
        After the video wall finishes:
        navbar sticks to top.
      */
      fixNav();

      /*
        Keep visible just after it appears,
        then hide on scroll down / show on scroll up.
      */
      if (currentY <= brandEndY + navHeight) {
        nav.classList.remove('is-hidden');
      } else if (delta > 6) {
        nav.classList.add('is-hidden');
      } else if (delta < -6) {
        nav.classList.remove('is-hidden');
      }

      lastY = Math.max(currentY, 0);
      ticking = false;
    }

    function requestUpdate() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    function refresh() {
      measure();
      requestUpdate();
    }

    measure();
    update();

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', refresh);

    document.addEventListener('shopify:section:load', function () {
      window.setTimeout(refresh, 150);
    });

    window.addEventListener('load', function () {
      window.setTimeout(refresh, 150);
    });
  }

  function initKoLeeNav() {
    document.querySelectorAll('[data-ko-lee-nav]').forEach(setupKoLeeNav);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKoLeeNav);
  } else {
    initKoLeeNav();
  }

  document.addEventListener('shopify:section:load', initKoLeeNav);
})();
